import React from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import { IStats } from "../shared/types";
import { AlertShell, AlertField } from "./ui/AlertShell";
import { AlertBotTitle } from "./LowGasAlert";
import EnhancedPriceRangeVisualizer, { rangeStatus } from "./PriceRangeVisualizer";
import { chainName, chainOf } from "./ui/ChainAvatar";
import { BOT_STATE_LABEL, botState, rangePrice } from "../utils/botMetrics";
import { formatDateTime, formatPairPrice } from "../utils/formatters";

/** "just now", "47m ago", "5h ago", "3d ago". */
const timeAgo = (ts: number, now = Date.now()): string => {
  const minutes = Math.max(0, Math.floor((now - ts) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
};

interface OutOfRangeAlertProps {
  stats: IStats;
}

/**
 * Critical (red) alert for a bot that earns no fees: its open position has left its range (how far past
 * which bound, the last activity and the range bar), or it has no open position and isn't balancing.
 * Renders nothing in any other state.
 */
export const OutOfRangeAlert: React.FC<OutOfRangeAlertProps> = ({ stats: s }) => {
  const theme = useTheme();
  const state = botState(s);
  if (state !== "out-of-range" && state !== "idle") return null;

  const outOfRange = state === "out-of-range";
  const color = theme.palette.error.main;
  // Same sentence as the range bar's status line: "12.4% below floor", "0.8% above ceiling".
  const status = outOfRange ? rangeStatus(s.rangeLower, s.rangeUpper, rangePrice(s)) : null;
  const lastActivity = s.lastActivityAt ?? s.positionLastUpdated;
  const formatPrice = (v: number) => formatPairPrice(v, s.token0Symbol, s.token1Symbol, s.decimals);

  const fields: AlertField[] = [];
  if (!outOfRange) fields.push({ label: "Pool", value: "No open position", color });
  else if (status) fields.push({ label: "Price", value: status.text, color });
  if (lastActivity && Number.isFinite(lastActivity)) {
    fields.push({
      label: "Last activity",
      value: <span title={formatDateTime(lastActivity)}>{timeAgo(lastActivity)}</span>,
    });
  }

  return (
    <AlertShell
      color={color}
      icon={
        !outOfRange ? (
          <RemoveCircleOutlineRoundedIcon />
        ) : status?.side === "floor" ? (
          <TrendingDownRoundedIcon />
        ) : (
          <TrendingUpRoundedIcon />
        )
      }
      title={<AlertBotTitle botId={s.botName} />}
      // Same pattern as the other operational alerts: "<state> · <network>".
      subtitle={`${BOT_STATE_LABEL[state]} · ${chainName(chainOf(s.botName))}`}
      fields={fields}
      trailing={
        outOfRange ? (
          // Full width on phones; a fixed column beside the figures once the alert is wide enough.
          <Box sx={{ width: "100%", "@container (min-width: 600px)": { width: 340 } }}>
            <EnhancedPriceRangeVisualizer
              min={s.rangeLower}
              max={s.rangeUpper}
              current={rangePrice(s)}
              entry={s.entryPrice}
              decimals={s.decimals ?? 2}
              formatPrice={formatPrice}
              showStatus={false}
            />
          </Box>
        ) : undefined
      }
    />
  );
};

export default OutOfRangeAlert;
